import axios, { AxiosResponse } from 'axios'
import { format, addYears } from 'date-fns'

declare const TESA_SUPPLEMENT_TEMPLATE_ID_PGE: string
declare const TESA_SUPPLEMENT_TEMPLATE_ID_SDGE: string
declare const TESA_SUPPLEMENT_TEMPLATE_ID_SCE: string

export const getProspectApi = async (
  prospectId: string
): Promise<AxiosResponse> =>
  await axios.get(`/api/public/v1/onboarding/invitation/${prospectId}/prospect`)

export const acceptAgreementApi = async (
  agreementType: keyof AgreementTypes,
  payload: AcceptAgreementRequest
): Promise<AxiosResponse> =>
  await axios.post(
    `/api/public/v1/onboarding/agreement/${agreementType}/accept`,
    payload
  )

export const declineOptiRateAmendmentApi = async (
  inviteCode: string
): Promise<AxiosResponse<{ status: 'DECLINED' }>> =>
  await axios.post('/api/public/v1/onboarding/agreement/TESA_OPTIRATE_AMENDMENT/decline', {
    inviteCode,
  })

const templateMap: { [key: string]: string } = {
  'PG&E': TESA_SUPPLEMENT_TEMPLATE_ID_PGE,
  'SDG&E': TESA_SUPPLEMENT_TEMPLATE_ID_SDGE,
  SCE: TESA_SUPPLEMENT_TEMPLATE_ID_SCE,
}

export const createSignatureRequestApi = async (
  prospect: Prospect
): Promise<CreateSignatureResponse> => {
  const {
    contact: { firstName, lastName, email, phone, title },
    tenant: { utilityAccounts, utilityAccountIdsUserInput },
    utility,
    utilityReplyToEmail,
  } = prospect
  const businessName = utilityAccounts[0].name
  const templateIds = [templateMap[utility]]

  let authorizedAccounts: Array<{
    serviceAddress: string
    accountNumber: string
  }> = []

  const getServiceAddress = (sa: Address, withStreet2 = true): string => {
    let text = sa?.street1
    if (withStreet2) {
      text += ` ${sa?.street2 ?? ''}`
    }
    text += `, ${sa?.city ?? ''}`
    return text
  }

  // Generate a more parsable accounts structure
  /*
    const accountsParsable = [{
      type: 'UTILITY_ACCOUNT',
      number: '5578362555',
      address: '111 Main St STA A, San Bernardino'
    }, {
      type: 'UTILITY_ACCOUNT',
      number: '5578362556',
      address: '111 Main St STA B, San Bernardino'
    }, {
      type: 'UTILITY_ACCOUNT',
      number: '5578362557',
      address: '111 Main St STA C, San Bernardino'
    }, {
      type: 'SERVICE_ACCOUNT',
      number: '805578362520',
      address: '222 Main St STA A, San Bernardino'
    }, {
      type: 'SERVICE_ACCOUNT',
      number: '805578362521',
      address: '222 Main St STA B, San Bernardino'
    }, {
      type: 'SERVICE_ACCOUNT',
      number: '805578362522',
      address: '222 Main St STA C, San Bernardino'
    }, {
      type: 'USER_INPUT',
      number: '805578362523',
      address: '111 Main St STA A, San Bernardino'
    }]
  */

  interface ParsableAccount {
    type: string
    number: string
    address: string
  }

  const accountsParsable: ParsableAccount[] = []

  utilityAccounts.forEach((ua: UtilityAccount) => {
    accountsParsable.push({
      type: 'UTILITY_ACCOUNT',
      number: ua.number,
      address: getServiceAddress(ua.serviceAgreements[0].serviceAddress),
    })

    ua.serviceAgreements.forEach((sa: ServiceAgreement) => {
      accountsParsable.push({
        type: 'SERVICE_ACCOUNT',
        number: sa.serviceAgreementId,
        address: getServiceAddress(sa.serviceAddress),
      })
    })
  })

  accountsParsable.push(
    ...((utilityAccountIdsUserInput ?? []).map((userInput: string) => {
      return {
        type: 'USER_INPUT',
        number: userInput,
        address:
          accountsParsable.filter(
            (pa: ParsableAccount) => pa.number === userInput
          )?.[0]?.address ??
          accountsParsable.filter(
            (pa: ParsableAccount) =>
              pa.type ===
              (utility === 'SCE' ? 'SERVICE_ACCOUNT' : 'UTILITY_ACCOUNT')
          )?.[0].address,
      }
    }) as ParsableAccount[])
  )

  const hasUserInput = Object.keys(utilityAccountIdsUserInput ?? {}).length > 0

  const accountsToParse = accountsParsable.filter((pa: ParsableAccount) => {
    if (utility === 'SCE') {
      return pa.type === 'SERVICE_ACCOUNT'
    } else if (hasUserInput) {
      return pa.type === 'USER_INPUT'
    } else {
      return pa.type === 'UTILITY_ACCOUNT'
    }
  })

  accountsToParse.forEach((pa) => {
    authorizedAccounts.push({
      accountNumber: pa.number,
      serviceAddress: pa.address,
    })
  })

  const uniqueAuthorizedAccounts = authorizedAccounts
    .map((acc) => acc.accountNumber)
    .filter((v, i, a) => a.indexOf(v) === i)
  const authorizeSpecificAccountsOnly = prospect.utility === 'PG&E' &&
    (prospect.tenant.authorizeSpecificAccountsOnly ?? false)
  const hasOnlyOneAccount = uniqueAuthorizedAccounts.length === 1 && !authorizeSpecificAccountsOnly
  const hasPage2OverflowAccounts =
    authorizedAccounts.length > (utility === 'SDG&E' ? 2 : 3)
  const totalAccountQty = hasPage2OverflowAccounts
    ? `(${authorizedAccounts.length} ACCOUNTS TOTAL)`
    : ''

  const accountNumber: string = hasOnlyOneAccount
    ? uniqueAuthorizedAccounts[0]
    : ''

  if (utility === 'PG&E' && hasOnlyOneAccount) {
    authorizedAccounts = []
  }

  let createSignatureRequestPayload: CreateSignatureRequest | {}

  switch (utility) {
    case 'PG&E':
      createSignatureRequestPayload = {
        templateIds,
        signers: [
          {
            email_address: email,
            name: `${firstName} ${lastName}`,
            role: 'Tenant',
          },
        ],
        fields: {
          fullName: `${firstName} ${lastName}`,
          phone,
          businessName:
            businessName != null && businessName !== '' ? businessName : '',
          title: title != null && title !== '' ? title : '',
          ...authorizedAccounts.reduce(
            (p, v, i) => ({
              ...p,
              [`serviceAddress${i + 1}`]: v.serviceAddress,
              [`accountNumber${i + 1}`]: v.accountNumber,
            }),
            {}
          ),
          hasOnlyOneAccount,
          hasMultipleAccounts: !hasOnlyOneAccount,
          hasPage2OverflowAccounts,
          totalAccountQty,
          accountNumber,
        },
      }
      break
    case 'SDG&E':
    case 'SCE':
      createSignatureRequestPayload = {
        templateIds,
        signers: [
          {
            email_address: email,
            name: `${firstName} ${lastName}`,
            role: 'Tenant',
          },
        ],
        fields: {
          fullName: `${firstName} ${lastName}`,
          phone,
          businessName:
            businessName != null && businessName !== '' ? businessName : '',
          title: title != null && title !== '' ? title : '',
          mailAddrStreet:
            utilityAccounts?.[0]?.billingAddress.street2 != null &&
            utilityAccounts?.[0]?.billingAddress.street2 !== ''
              ? `${utilityAccounts?.[0]?.billingAddress.street1}, ${utilityAccounts?.[0]?.billingAddress.street2}`
              : utilityAccounts?.[0]?.billingAddress.street1,
          mailAddrCity: utilityAccounts[0].billingAddress.city,
          mailAddrState: utilityAccounts[0].billingAddress.state,
          mailAddrZip: utilityAccounts[0].billingAddress.zip,
          ...(utility === 'SCE'
            ? { execUntil: format(addYears(new Date(), 3), 'P') }
            : undefined),
          execDay: format(new Date(), 'do'),
          execMonth: format(new Date(), 'MMMM'),
          execYear: format(new Date(), 'yyyy'),
          utilityReplyToEmail,
          ...authorizedAccounts.reduce(
            (p, v, i) => ({
              ...p,
              [`serviceAddress${i + 1}`]: v.serviceAddress,
              [`accountNumber${i + 1}`]: v.accountNumber,
            }),
            {}
          ),
          hasPage2OverflowAccounts,
          totalAccountQty,
        },
      }
      break
    default:
      createSignatureRequestPayload = {}
  }

  return await axios
    .post(
      '/api/public/v1/onboarding/agreement/TESA/link',
      createSignatureRequestPayload
    )
    .then((res: AxiosResponse) => res.data as CreateSignatureResponse)
}
